// ** React Imports
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

/**
 * LocationContext
 * 
 * Provides location data and management across the app
 * - Gets user's current location via geolocation API
 * - Allows manual location selection
 * - Reverse geocodes coordinates to address
 * - Shares location state with all components
 */

const defaultProvider = {
  location: null,
  locationName: '',
  isLoading: true,
  setLocation: () => null,
  setLocationName: () => null,
  refreshLocation: () => Promise.resolve(),
};

const LocationContext = createContext(defaultProvider);

export const LocationProvider = ({ children }) => {
  // State for coordinates and location name
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Reverse geocode coordinates to get address/city name
   * Uses OpenStreetMap Nominatim API (free, no API key required)
   */
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      
      const address = response.data.address;
      // Try to get city, town, or village name
      const cityName = address.city || address.town || address.village || address.county || 'unknown location';
      
      return cityName;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return 'unknown location';
    }
  };

  /**
   * Get current location from browser geolocation API
   */
  const getCurrentLocation = async () => {
    setIsLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          
          setLocation(coords);
          
          // Get location name
          const name = await reverseGeocode(coords.lat, coords.lng);
          setLocationName(name);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Fallback to Dubai
          const fallbackCoords = { lat: 25.2048, lng: 55.2708 };
          setLocation(fallbackCoords);
          setLocationName('dubai');
          setIsLoading(false);
        }
      );
    } else {
      // Geolocation not supported - use fallback
      const fallbackCoords = { lat: 25.2048, lng: 55.2708 };
      setLocation(fallbackCoords);
      setLocationName('dubai');
      setIsLoading(false);
    }
  };

  /**
   * Update location manually (from map selection)
   */
  const updateLocation = async (lat, lng) => {
    setIsLoading(true);
    const coords = { lat, lng };
    setLocation(coords);
    
    // Get location name
    const name = await reverseGeocode(lat, lng);
    setLocationName(name);
    setIsLoading(false);
  };

  // Get location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  const values = {
    location,
    locationName,
    isLoading,
    setLocation: updateLocation,
    setLocationName,
    refreshLocation: getCurrentLocation,
  };

  return (
    <LocationContext.Provider value={values}>
      {children}
    </LocationContext.Provider>
  );
};

// Custom hook to use location context
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export default LocationContext;




